import { YachtForm } from "./yacht.schema";
import { Button } from "@/src/components/ui/button";
import { Separator } from "@/src/components/ui/separator";
import { Card } from "@/src/components/wrappers/Card";
import { useFormContext } from "react-hook-form";
import { useStepsStore } from "./state";
import { extractFromMultiName } from "../../Locations/utils";
import { YachtDetails } from "../model";
import { useLoaderData } from "react-router-dom";
import { fetch } from "@/src/lib/fetch";
import { useState, useEffect } from "react";
import { evalExtraPriceSimplified } from "@/src/app/Offers/EditOffer/YachtDetailsModal";
import { fetchAvailability, Availability } from "../../Offers/EditOffer/useOldSearch";

export function Summary() {
  const formValue = useFormContext<YachtForm>().getValues();
  const amenities = formValue.amenities;
  const yacht = useLoaderData() as YachtDetails;
  const [availability, setAvailability] = useState<Availability[]>([]);

  const equipment = amenities.filter((a) => !a.formula?.trim());
  const extras = amenities.filter((a) => !!a.formula?.trim());
  const { setStep } = useStepsStore();

  const location = yacht?.location?.names;
  const clientPrice = 0;

  useEffect(() => {
    async function getAvailability() {
      setAvailability([]);
      if (yacht?.id) {
        return fetchAvailability(yacht?.id).then(setAvailability);
      }
    }
    if (yacht?.id) {
      getAvailability();
    }
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col items-stretch">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Basic details</h3>
            <Button variant="link" size="sm" onClick={() => setStep("Basic details")}>
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-6">
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Yacht name</span>
              <span className="text-base font-medium">{formValue.basicDetails.name}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Yacht model</span>
              <span className="text-base font-medium">{formValue.basicDetails.model?.name}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Yacht operator</span>
              <span className="text-base font-medium">{formValue.basicDetails.operator?.name}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Yacht manufacturer</span>
              <span className="text-base font-medium">{formValue.basicDetails.manufacturer}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Production year</span>
              <span className="text-base font-medium">{formValue.basicDetails.productionYear}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Yacht type</span>
              <span className="text-base font-medium">{formValue.basicDetails.type}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Start day</span>
              <span className="text-base font-medium">{formValue.basicDetails.startDay}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Minimum duration (days)</span>
              <span className="text-base font-medium">{formValue.basicDetails.minimumCharterDuration}</span>
            </div>

            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Charter type</span>
              <span className="text-base font-medium">
                {formValue.basicDetails.charterTypes.bareboat
                  ? "Bareboat"
                  : formValue.basicDetails.charterTypes.skippered
                    ? "Skippered"
                    : formValue.basicDetails.charterTypes.crewed
                      ? "Crewed"
                      : null}
              </span>
            </div>

            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Charter start day</span>
              <span className="text-base font-medium">{formValue.basicDetails.startDay}</span>
            </div>

            <div className="flex flex-col gap-1 items-stretch col-span-full">
              <span className="text-base font-normal">Description</span>
              <span className="text-base font-medium">{formValue.basicDetails.description}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch col-span-full">
              <span className="text-base font-normal">Uploaded images</span>
              <div className="flex gap-4 items-center">
                {formValue.basicDetails.photos.map((f) => (
                  <div key={f.id} className="h-24 w-40 overflow-hidden rounded-md border border-slate-300">
                    <img src={f.url} alt={f.name} className="object-contain h-full w-full" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1 items-stretch col-span-full">
              <span className="text-base font-normal">Broker notes</span>
              <span className="text-base font-medium">{formValue.basicDetails.notes}</span>
            </div>
            <Separator className="col-span-full" />
            <span className="text-base font-normal col-span-full">Prices</span>
            {formValue.locationAndPricing.pricing.type === "simple" && (
              <span className="text-base font-medium">
                {formValue.locationAndPricing.pricing.price} {formValue.locationAndPricing.pricing.currency} per{" "}
                {formValue.locationAndPricing.pricing.per}
              </span>
            )}
            {formValue.locationAndPricing.pricing.type === "seasonal" &&
              formValue.locationAndPricing.pricing.prices.map((p, i) => (
                <div key={i} className="flex flex-col gap-1 items-stretch col-span-full">
                  <span className="text-base font-normal">
                    {p.dateRange.startDate} - {p.dateRange.endDate}
                  </span>
                  <span className="text-sm text-slate-600">
                    {p.locations.map((location) => extractFromMultiName(location.names)).join(", ")}
                  </span>
                  <span className="text-base font-medium">
                    {p.price} {p.currency} per {p.per}
                  </span>
                </div>
              ))}
            <div className="flex flex-col gap-1 items-stretch col-span-full">
              <span className="text-base font-normal">Security deposit</span>
              <span className="text-base font-medium">
                {formValue.locationAndPricing.securityDeposit.price}{" "}
                {formValue.locationAndPricing.securityDeposit.currency}
              </span>
            </div>
          </div>
        </div>
      </Card>
      <Card>
        <div className="flex flex-col items-stretch">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Locations</h3>
            <Button variant="link" size="sm" onClick={() => setStep("Location & Pricing")}>
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-medium">
                {formValue.locationAndPricing?.pricing?.type === "simple"
                  ? extractFromMultiName(formValue.locationAndPricing.pricing.location?.names) || "N/A"
                  : `${formValue.locationAndPricing.pricing.prices.length} configured rate periods`}
              </span>
            </div>
          </div>
        </div>
      </Card>
      <Card>
        <div className="flex flex-col items-stretch">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Specification</h3>
            <Button variant="link" size="sm" onClick={() => setStep("Specification")}>
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-6">
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-normal">Guests</span>
              <span className="text-base font-medium">{formValue.specification.maxPeople}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-normal">Cabins</span>
              <span className="text-base font-medium">{formValue.specification.cabins}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-normal">Berths</span>
              <span className="text-base font-medium">{formValue.specification.berths}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-normal">Heads</span>
              <span className="text-base font-medium">{formValue.specification.heads}</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-normal">Length</span>
              <span className="text-base font-medium">{formValue.specification.length} m</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch ">
              <span className="text-base font-normal">Beam</span>
              <span className="text-base font-medium">{formValue.specification.beam} m</span>
            </div>
            <div className="flex flex-col gap-1 items-stretch">
              <span className="text-base font-normal">Mainsail</span>
              <span className="text-base font-medium">{formValue.specification.mainsailType}</span>
            </div>
          </div>
          {!!formValue.specification.extraSpecs.length && (
            <>
              <div className="mt-6 flex justify-between items-center">
                <h3 className="text-md font-semibold mb-4">Additional specification</h3>
              </div>
              <div className="grid grid-cols-4 gap-6">
                {formValue.specification.extraSpecs.map((spec, i) => (
                  <div className="flex flex-col gap-1 items-stretch" key={i}>
                    <span className="text-base font-normal">{spec.name}</span>
                    <span className="text-base font-medium">{spec.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Card>
      <Card>
        <div className="flex flex-col items-stretch">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Selected amenities</h3>
            <Button variant="link" size="sm" onClick={() => setStep("Amenities")}>
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {!!equipment.length && (
              <div className="flex flex-col gap-4">
                <h4 className="text-l">Equipment</h4>
                {equipment.map((a, i) => (
                  <div
                    key={i}
                    className="flex flex-col p-4 border border-blue-100 bg-slate-50 rounded-sm items-stretch"
                  >
                    <div className="flex gap-2 items-center">
                      <span>{a.name}</span>
                      {a.obligatory && <span className="text-sm bg-blue-100 rounded-3xl py-1 px-2">Obligatory</span>}
                    </div>
                    {a.formula && (
                      <>
                        <span className="font-light text-sm mt-1">
                          + {evalExtraPriceSimplified(a.formula, location, clientPrice)}
                        </span>
                        <span className="font-light text-sm">paid {!a.paid_locally ? "in advance" : "locally"}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!!extras.length && (
              <div className="flex flex-col gap-4">
                <h4 className="text-l">Extras</h4>
                {extras.map((a, i) => (
                  <div
                    key={i}
                    className="flex flex-col p-4 border border-blue-100 bg-slate-50 rounded-sm items-stretch"
                  >
                    <div className="flex gap-2 items-center">
                      <span>{a.name}</span>
                      {a.obligatory && <span className="text-sm bg-blue-100 rounded-3xl py-1 px-2">Obligatory</span>}
                    </div>
                    {a.formula && (
                      <>
                        <span className="font-light text-sm mt-1">
                          + {evalExtraPriceSimplified(a.formula, location, clientPrice)}
                        </span>
                        <span className="font-light text-sm">paid {!a.paid_locally ? "in advance" : "locally"}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
